"use client"

import { SOURCE_FILTER_ROW, sourceFilterPill } from "@/components/source-filter-pill"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ChevronDown } from "@/lib/icons"
import { RULE34_SCORE_TIERS, RULE34_WINDOWS } from "@/lib/rule34-filter-terms"
import { useTranslations } from "next-intl"

interface Rule34FilterTabsProps {
  scoreMin: number | null
  onScoreChange: (value: number | null) => void
  days: number | null
  onDaysChange: (value: number | null) => void
}

const ANY = "any"

// Score tiers and best-of windows are what rule34.dev offers in its sort menu. They are two dropdowns, not a row of pills:
// together they are 19 choices, far wider than the shared source filter bar. Each button shows what is chosen, and
// highlights like an active pill when something other than "any" is picked.
export function Rule34FilterTabs({ scoreMin, onScoreChange, days, onDaysChange }: Rule34FilterTabsProps) {
  const t = useTranslations("rule34FilterTabs")
  const tier = RULE34_SCORE_TIERS.find((item) => item.min === scoreMin)
  const window = RULE34_WINDOWS.find((item) => item.days === days)

  return (
    <div className={SOURCE_FILTER_ROW}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className={`${sourceFilterPill(scoreMin !== null)} inline-flex items-center gap-1`}>
            {t("score")}: {tier ? tier.label : t("any")}
            <ChevronDown className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[11rem]">
          <DropdownMenuRadioGroup
            value={scoreMin === null ? ANY : String(scoreMin)}
            onValueChange={(value) => onScoreChange(value === ANY ? null : Number(value))}
          >
            <DropdownMenuRadioItem value={ANY}>{t("any")}</DropdownMenuRadioItem>
            {RULE34_SCORE_TIERS.map((item) => (
              <DropdownMenuRadioItem key={item.min} value={String(item.min)}>
                {item.name}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className={`${sourceFilterPill(days !== null)} inline-flex items-center gap-1`}>
            {t("bestOf")}: {window ? window.label : t("allTime")}
            <ChevronDown className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[9rem]">
          <DropdownMenuRadioGroup
            value={days === null ? ANY : String(days)}
            onValueChange={(value) => onDaysChange(value === ANY ? null : Number(value))}
          >
            <DropdownMenuRadioItem value={ANY}>{t("allTime")}</DropdownMenuRadioItem>
            {RULE34_WINDOWS.map((item) => (
              <DropdownMenuRadioItem key={item.days} value={String(item.days)}>
                {item.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
